import { partition, uniq, unzip } from 'es-toolkit';

import { groupTransitively, notNullish } from '@paragon/utilities';

import Plane3D from './Plane3D';
import Polygon3D from './Polygon3D';
import Polyhedron from './Polyhedron';
import { distanceEquals } from './units';
import { groupSegmentsTransitively } from './util';

class Polygons {
  readonly faces: readonly Polygon3D[];

  constructor(faces: readonly Polygon3D[]) {
    this.faces = faces;
  }

  toPolyhedron(): Polyhedron {
    return Polyhedron.fromFaces(this.faces);
  }

  private slicePartitioned(
    slicingPlane: Plane3D,
    computeOppositeSideSolids = true,
  ): { normalSideSolids: Polygons[]; oppositeSideSolids: Polygons[] } {
    const [uncollectedFacesOnNormalSide, uncollectedFacesOnOppositeSide] = unzip(
      this.faces.map((face) => {
        const faceSides = uniq(
          face.vertices
            .map((vertex) => {
              const projectedDistance = slicingPlane.signedDistanceTo(vertex);
              return distanceEquals(projectedDistance, 0) ? null : projectedDistance > 0;
            })
            .filter(notNullish),
        );
        switch (faceSides.length) {
          case 2: // true and false means the face crosses the slicing plane
            return partition(face.slice(slicingPlane), (faceSlice) =>
              faceSlice.vertices.some(slicingPlane.pointIsOnNormalSide.bind(slicingPlane)),
            );
          case 1: // true or false means the plane lies entirely on one side of the slicing plane
            return faceSides[0] ? [[face], []] : [[], [face]];
          default: // neither true nor false means the face lies within the slicing plane and will be reconstructed later
            return [[], []];
        }
      }),
    );

    const formPolyhedra = (uncollectedFaces: Polygon3D[][]): Polygons[] =>
      groupTransitively(uncollectedFaces.flat(), (face1, face2) =>
        face1.edges.some((edge1) => face2.edges.some((edge2) => edge1.equals(edge2))),
      ).map(
        (faces) =>
          new Polygons(
            groupTransitively(
              groupSegmentsTransitively(faces.flatMap(({ edges }) => edges))
                .filter((group) => group.length === 1)
                .map(([edge]) => edge),
              (edge1, edge2) => edge1.endPoints.some((point) => edge2.endPoints.some(point.equals.bind(point))),
            )
              .map((edgeGroup) => {
                try {
                  return Polygon3D.fromUnorientedOrUnorderedSegments(edgeGroup).reverse();
                } catch {
                  return null;
                }
              })
              .filter(notNullish)
              .concat(faces),
          ),
      );

    const validatePolyhedra: (polyhedra: Polygons[]) => void = () => {
      //     if (polyhedra.AllPairs().Any((solids) => solids.Item1.OverlappingPolygon(solids.Item2) is not null))
      //         throw new Exception("Slicing across a cross section with a hole in it is unsupported");
      // TODO
    };

    const normalSideSolids = formPolyhedra(uncollectedFacesOnNormalSide);
    validatePolyhedra(normalSideSolids);
    const oppositeSideSolids = computeOppositeSideSolids ? formPolyhedra(uncollectedFacesOnOppositeSide) : [];
    validatePolyhedra(oppositeSideSolids);
    return { normalSideSolids, oppositeSideSolids };
  }

  trim(slicingPlane: Plane3D): Polygons[] {
    return this.slicePartitioned(slicingPlane, false).normalSideSolids;
  }

  trimByMany(slicingPlanes: readonly Plane3D[]): Polygons[] {
    return slicingPlanes.reduce<Polygons[]>(
      (solids, slicingPlane) => solids.flatMap((solid) => solid.trim(slicingPlane)),
      [this],
    );
  }
}

export default Polygons;
